import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Heart, Home, Calendar, Stethoscope, Star, Quote, ArrowRight,
  PawPrint, Users, Phone, Mail, MapPin
} from 'lucide-react';

// Icon mapping for features
const ICON_MAP = {
  heart: Heart,
  home: Home,
  calendar: Calendar,
  medical: Stethoscope,
  star: Star,
  default: Star
};

// Render individual section based on type
const SectionRenderer = ({ section, primaryColor, secondaryColor }) => {
  if (!section || !section.visible) return null;

  const content = section.content || {};

  switch (section.type) {
    case 'hero':
      return (
        <div 
          className="relative py-24 text-center text-white"
          style={{ 
            backgroundImage: content.backgroundImage 
              ? `url(${content.backgroundImage})` 
              : `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {content.overlay && <div className="absolute inset-0 bg-black/40" />}
          <div className="relative z-10 container mx-auto px-6">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">{content.headline}</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">{content.subheadline}</p>
            {content.buttonText && (
              <Button 
                size="lg" 
                className="text-white"
                style={{ backgroundColor: secondaryColor }}
                onClick={() => {
                  if (content.buttonLink?.startsWith('#')) {
                    document.querySelector(content.buttonLink)?.scrollIntoView({ behavior: 'smooth' });
                  } else if (content.buttonLink) {
                    window.location.href = content.buttonLink;
                  }
                }}
              >
                {content.buttonText}
              </Button>
            )}
          </div>
        </div>
      );

    case 'about':
      return (
        <div id="about" className="container mx-auto px-6 py-16">
          <div className={`grid ${content.image ? 'md:grid-cols-2 gap-12 items-center' : ''}`}>
            {content.imagePosition === 'left' && content.image && (
              <img 
                src={content.image} 
                alt={content.title} 
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
            )}
            <div className={content.image ? '' : 'text-center max-w-3xl mx-auto'}>
              <h3 className="text-3xl font-bold mb-4" style={{ color: primaryColor }}>{content.title}</h3>
              <p className="text-lg text-stone-600 leading-relaxed">{content.content}</p>
            </div>
            {content.imagePosition === 'right' && content.image && (
              <img 
                src={content.image} 
                alt={content.title} 
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
            )}
          </div>
        </div>
      );

    case 'features':
      return (
        <div className="py-16 bg-stone-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>{content.title}</h3>
              {content.subtitle && <p className="text-lg text-stone-600">{content.subtitle}</p>}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(content.items || []).map((item, idx) => {
                const Icon = ICON_MAP[item.icon] || ICON_MAP.default;
                return (
                  <Card key={idx} className="text-center hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div 
                        className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                        style={{ backgroundColor: `${primaryColor}15` }}
                      >
                        <Icon className="w-7 h-7" style={{ color: primaryColor }} />
                      </div>
                      <h4 className="font-semibold mb-2">{item.title}</h4>
                      <p className="text-sm text-stone-600">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      );

    case 'gallery':
      if (!content.images || content.images.length === 0) return null;
      const cols = content.columns || 3;
      return (
        <div className="container mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>{content.title}</h3>
            {content.subtitle && <p className="text-lg text-stone-600">{content.subtitle}</p>}
          </div>
          <div className={`grid grid-cols-2 md:grid-cols-${cols} gap-4`}>
            {content.images.map((img, idx) => (
              <div key={idx} className="aspect-square overflow-hidden rounded-lg">
                <img 
                  src={img.url} 
                  alt={img.caption || `Gallery image ${idx + 1}`} 
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
              </div>
            ))}
          </div>
        </div>
      );

    case 'testimonials':
      return (
        <div className="py-16 bg-stone-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>{content.title}</h3>
              {content.subtitle && <p className="text-lg text-stone-600">{content.subtitle}</p>}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(content.items || []).map((item, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <Quote className="w-8 h-8 mb-4 opacity-20" style={{ color: primaryColor }} />
                    <p className="text-stone-600 italic mb-4">&ldquo;{item.quote}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                          <Users className="w-5 h-5 text-stone-400" />
                        </div>
                      )}
                      <span className="font-medium">{item.name}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      );

    case 'cta':
      return (
        <div 
          className="py-16 text-center text-white"
          style={{ backgroundColor: content.backgroundColor === 'primary' ? primaryColor : secondaryColor }}
        >
          <div className="container mx-auto px-6">
            <h3 className="text-3xl font-bold mb-4">{content.title}</h3>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">{content.subtitle}</p>
            {content.buttonText && (
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-stone-900"
                onClick={() => {
                  if (content.buttonLink) window.location.href = content.buttonLink;
                }}
              >
                {content.buttonText} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      );

    case 'stats':
      return (
        <div className="py-16" style={{ backgroundColor: `${primaryColor}08` }}>
          <div className="container mx-auto px-6">
            {content.title && (
              <h3 className="text-3xl font-bold mb-12 text-center" style={{ color: primaryColor }}>{content.title}</h3>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {(content.items || []).map((item, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: primaryColor }}>{item.number}</p>
                  <p className="text-stone-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );

    case 'team':
      return (
        <div className="container mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>{content.title}</h3>
            {content.subtitle && <p className="text-lg text-stone-600">{content.subtitle}</p>}
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {(content.members || []).map((member, idx) => (
              <Card key={idx} className="text-center overflow-hidden">
                <div className="aspect-square bg-stone-100">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="w-16 h-16 text-stone-300" />
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <h4 className="font-semibold">{member.name}</h4>
                  <p className="text-sm" style={{ color: primaryColor }}>{member.role}</p>
                  {member.bio && <p className="text-xs text-stone-500 mt-2">{member.bio}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );

    case 'contact':
      return (
        <div className="container mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>{content.title}</h3>
            {content.subtitle && <p className="text-lg text-stone-600">{content.subtitle}</p>}
          </div>
          {/* Contact info would be rendered from website contact data */}
        </div>
      );

    case 'custom':
      return (
        <div 
          className="py-16"
          style={{ backgroundColor: content.backgroundColor === 'primary' ? `${primaryColor}08` : 'white' }}
        >
          <div className="container mx-auto px-6">
            {content.title && (
              <h3 className="text-3xl font-bold mb-6 text-center" style={{ color: primaryColor }}>{content.title}</h3>
            )}
            <div className="max-w-3xl mx-auto text-lg text-stone-600 text-center">
              {content.content}
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

// Main component to render all sections
const WebsiteSections = ({ sections = [], primaryColor, secondaryColor }) => {
  const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return (
    <>
      {sortedSections.map((section) => (
        <SectionRenderer 
          key={section.id} 
          section={section} 
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      ))}
    </>
  );
};

export { SectionRenderer, WebsiteSections };
export default WebsiteSections;
